import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { MetadataPropTypesShape } from '@Components/Results/MetadataPropTypes';
import DocumentCustomLink from '@Components/Results/documents/DocumentCustomLink';
import FullText from '@Components/Results/reactions/reaction-details/FullText';
import { buildDetailNavItems } from '@Components/Results/documents/DocumentExpandSections';
import ResultDetailNav from '@Components/Results/common/ResultDetailNav';

import './DocumentButtons.scss';

/**
 * Details navigation for a document result. A single data-driven nav row with the
 * detail categories and a trailing "Full Text" external link. Exclusive open state
 * is owned by the parent via `activeId` + `onToggle`. `navRef` is attached to the
 * nav container so the parent can return focus to a nav button when a card closes.
 */
function DocumentButtons({ document: citation, activeId, onToggle, onOpen, onClose, getTrackingAttrs, metadata, navRef }) {
  const ariaLabel = 'Document result details';
  const items = useMemo(() => {
    const navItems = buildDetailNavItems(citation).map((item) => ({
      ...item,
      attrs: getTrackingAttrs({
        'data-tracking': activeId !== item.id,
        'data-tracking-type': 'contentview,contentlinkclick',
        'data-tracking-action': item.title,
      }),
    }));

    const fullTextNode = citation.fullTextCustom ? (
      <DocumentCustomLink citation={citation} getTrackingAttrs={getTrackingAttrs} />
    ) : (
      <FullText
        citation={citation}
        trackingAttributes={{ index: citation.index, getTrackingAttrs: getTrackingAttrs() }}
        metadata={metadata}
      />
    );
    const fullTextItem = { id: 'fullText', node: fullTextNode };

    return [...navItems, fullTextItem];
  }, [citation, activeId, getTrackingAttrs, metadata]);

  return (
    <div className="result-detail-row-btns" ref={navRef}>
      <ResultDetailNav items={items} activeId={activeId} onToggle={onToggle} onOpen={onOpen} onClose={onClose} ariaLabel={ariaLabel} />
    </div>
  );
}

DocumentButtons.propTypes = {
  document: PropTypes.object.isRequired,
  activeId: PropTypes.string,
  onToggle: PropTypes.func.isRequired,
  onOpen: PropTypes.func,
  onClose: PropTypes.func,
  getTrackingAttrs: PropTypes.func.isRequired,
  metadata: MetadataPropTypesShape,
  // Ref to the nav container, owned by the parent so it can restore focus on close.
  navRef: PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
};

export default DocumentButtons;
